// Heuristic severity classifier for NJ municipal court offenses.
// Used to color-code records on the Cards view. Keyword/statute matching -
// fast, deterministic, no LLM call. The LLM commentary is the "smart" layer;
// this is just a visual scan aid.

export type Severity = "minor" | "moderate" | "serious" | "severe";

interface Rule {
  level: Severity;
  re: RegExp;
}

// Order matters - first match wins, so put the most-severe checks first.
const RULES: Rule[] = [
  // Severe - violence, weapons, person-on-person
  { level: "severe", re: /\b(assault|aggravated|robbery|burglary|kidnap|murder|homicide|manslaughter|rape|sex(ual)?\s+(assault|abuse)|weapon|firearm|terroristic|stalk)/i },
  { level: "severe", re: /\b2C:1(1|2|3|4|5|8)-|\b2C:39-/i },

  // Serious - drug distribution, DWI, repeat-offender energy
  { level: "serious", re: /distribut|intent\s+to\s+distribut|sale\s+of\s+(cds|controlled)/i },
  { level: "serious", re: /\b2C:35-5\b/i },
  { level: "serious", re: /\bdwi\b|\bdui\b|driving\s+(while|under)\s+(intox|influence)/i },
  { level: "serious", re: /pos+ess(ion)?\s+(of\s+)?(cocaine|heroin|fentanyl|meth)/i },

  // Moderate - drug possession, quality of life, license trouble
  { level: "moderate", re: /\b2C:35-10\b/i },
  { level: "moderate", re: /pos+ess(ion)?\s+.*(marijuana|hashish|cds|paraphernalia)/i },
  { level: "moderate", re: /loiter|wander|prowl/i },
  { level: "moderate", re: /public\s+(consumption|urination|urinating)/i },
  { level: "moderate", re: /disorderly|disturbance|trespass/i },
  { level: "moderate", re: /shoplift|theft\s+(by\s+deception|under)/i },
  { level: "moderate", re: /suspend|revoked\s+(license|registration)/i },

  // Minor - traffic, paperwork
  { level: "minor", re: /\b39:[34]-|traffic|seat\s*belt|red\s*light|stop\s*sign|signal|speed/i },
  { level: "minor", re: /careless|fail\s+to\s+(observe|stop|yield|signal|exhibit|provide|maintain|wear)/i },
  { level: "minor", re: /no\s+(insurance|liability|registration|inspection|license)/i },
  { level: "minor", re: /fare|dog|animal|park(ing)?|noise|smok(ing)?/i },
];

export function classifyOffense(text: string): Severity {
  if (!text) return "minor";
  for (const r of RULES) {
    if (r.re.test(text)) return r.level;
  }
  return "minor";
}

// ─────────────────────────────────────────────────────────────────────────
// Offense category - orthogonal to severity. Drives the watermark icon
// rendered behind each offense card. Categories are visual buckets for
// what the charge IS (vehicle, drug, weapon, etc.), not how severe.
// ─────────────────────────────────────────────────────────────────────────

export type OffenseCategory =
  | "vehicle"
  | "drug"
  | "weapon"
  | "assault"
  | "property"
  | "public-order"
  | "fraud"
  | "other";

interface CategoryRule {
  cat: OffenseCategory;
  re: RegExp;
}

// Order matters - first match wins. Most specific patterns first.
const CATEGORY_RULES: CategoryRule[] = [
  { cat: "weapon",       re: /\bweapon|firearm|gun|knife|deadly|2C:39-/i },
  { cat: "assault",      re: /\bassault|aggravated|battery|kidnap|murder|homicide|manslaughter|rape|sex(ual)?\s+(assault|abuse)|terroristic|stalk|harass|menac/i },
  { cat: "drug",         re: /\bcds|drug|cocain|heroin|fentanyl|meth|marijuan|hashish|paraphernali|narcotic|2C:35-/i },
  { cat: "fraud",        re: /\bfraud|forgery|deception|bad\s+check|writing\s+bad|theft\s+of\s+services|finance/i },
  { cat: "property",     re: /\btheft|burglary|robbery|larcen|shoplift|trespass|criminal\s+mischief/i },
  { cat: "vehicle",      re: /\b39:|\bdwi\b|\bdui\b|drivi?ng|motor\s*veh|vehicle|speed|license|registration|insurance|park(ing)?|seat\s*belt|red\s*light|stop\s*sign|signal|inspection|tire/i },
  { cat: "public-order", re: /\bdisorder|loiter|wander|prowl|public\s+(consumption|urination|intox)|nuisance|disturb|noise|smok(ing)?|fare|dog|animal/i },
];

export function categorizeOffense(text: string): OffenseCategory {
  if (!text) return "other";
  for (const r of CATEGORY_RULES) {
    if (r.re.test(text)) return r.cat;
  }
  return "other";
}

export const SEVERITY_LABEL: Record<Severity, string> = {
  minor: "Minor",
  moderate: "Moderate",
  serious: "Serious",
  severe: "Severe",
};

// DDC-style "card themes" - each severity is one self-contained look:
// background, body text color, eyebrow color, accent. Serious & severe lean
// into solid bold color with WHITE text (so they pop in the grid like the
// hero stat in the DDC report). Minor & moderate stay tinted/cream-flavored
// against the dark page background, keeping a calmer presence.
export interface SeverityTheme {
  /** Tailwind classes for the card root: background + border. */
  bgClass: string;
  /** Body text class - white for solid cards, default for tinted. */
  textClass: string;
  /** Eyebrow & label color (severity tag, dot, etc.). */
  labelClass: string;
  /** Inline rgb hex / var for things that need a raw color. */
  accent: string;
  /** True when the card is solid-colored with white text (severe / serious). */
  bold: boolean;
}

export const SEVERITY_THEME: Record<Severity, SeverityTheme> = {
  minor: {
    bgClass:
      "bg-[color:var(--color-bg-card)] border-[color:var(--color-blue-hi)]/30",
    textClass: "text-[color:var(--color-text)]",
    labelClass: "text-[color:var(--color-blue-hi)]",
    accent: "var(--color-blue-hi)",
    bold: false,
  },
  moderate: {
    bgClass:
      "bg-[color:var(--color-gold-soft)] border-[color:var(--color-gold-hi)]/40",
    textClass: "text-[color:var(--color-text)]",
    labelClass: "text-[color:var(--color-gold-hi)]",
    accent: "var(--color-gold-hi)",
    bold: false,
  },
  serious: {
    bgClass:
      "bg-[color:var(--color-orange-hi)] border-[color:var(--color-orange-hi)]",
    textClass: "text-white",
    labelClass: "text-white/80",
    accent: "white",
    bold: true,
  },
  severe: {
    bgClass:
      "bg-[color:var(--color-rose-hi)] border-[color:var(--color-rose-hi)]",
    textClass: "text-white",
    labelClass: "text-white/80",
    accent: "white",
    bold: true,
  },
};

// Legacy aliases - older imports still work.
export const SEVERITY_BG: Record<Severity, string> = {
  minor: SEVERITY_THEME.minor.bgClass,
  moderate: SEVERITY_THEME.moderate.bgClass,
  serious: SEVERITY_THEME.serious.bgClass,
  severe: SEVERITY_THEME.severe.bgClass,
};

export const SEVERITY_TEXT: Record<Severity, string> = {
  minor: SEVERITY_THEME.minor.labelClass,
  moderate: SEVERITY_THEME.moderate.labelClass,
  serious: SEVERITY_THEME.serious.labelClass,
  severe: SEVERITY_THEME.severe.labelClass,
};
